import React, { useState } from "react";
import { Box, Grid, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, TextField } from "@mui/material";
import { AddCircleOutline, Edit, Check, Close } from "@mui/icons-material";

export interface Observacion { descripcion: string; fecha: string; usuario: string; }

export interface ObservationsSectionProps {
  observaciones: Observacion[];
  nuevaObservacion: Observacion;
  setNuevaObservacion: (o: Observacion) => void;
  agregarObservacion: () => void;
  editarObservacion?: (index: number, nueva: Observacion) => void;
}

export const ObservationsSection: React.FC<ObservationsSectionProps> = ({ observaciones = [], nuevaObservacion, setNuevaObservacion, agregarObservacion, editarObservacion }) => {
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editObs, setEditObs] = useState<Observacion>({ descripcion: '', fecha: '', usuario: '' });

  const renderDescripcion = (raw: string) => {
    const descripcion = (raw || '').toString();
    const lines = descripcion.split(/\r?\n/).filter(l => l !== '');
    if (lines.length <= 1) {
      const single = lines[0] ?? descripcion;
      const idx = single.indexOf(':');
      if (idx > 0) {
        const label = single.slice(0, idx + 1);
        const rest = single.slice(idx + 1);
        return (
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 600 }}>{label}</Box>
            {rest}
          </Typography>
        );
      }
      const isActionLine = /^(Se agregó pasajero|Se eliminó pasajero|Actualización)/i.test(single.trim());
      return <Typography variant="body2" sx={isActionLine ? { fontWeight: 600 } : undefined}>{single}</Typography>;
    }

    return (
      <Box>
        {lines.map((line, i) => {
          const idx = line.indexOf(':');
          if (i === 0) {
            return (
              <Typography key={i} variant="body2" sx={{ fontWeight: 600 }}>
                {line}
              </Typography>
            );
          }
          if (idx > 0) {
            const label = line.slice(0, idx + 1);
            const rest = line.slice(idx + 1);
            return (
              <Typography key={i} variant="body2">
                <Box component="span" sx={{ fontWeight: 600 }}>{label}</Box>
                {rest}
              </Typography>
            );
          }
          return <Typography key={i} variant="body2">{line}</Typography>;
        })}
      </Box>
    );
  };

  const startEdit = (idx: number, obs: Observacion) => {
    setEditIndex(idx);
    setEditObs(obs);
  };

  const cancelEdit = () => {
    setEditIndex(null);
    setEditObs({ descripcion: '', fecha: '', usuario: '' });
  };

  const saveEdit = (idx: number) => {
    if (editarObservacion) {
      editarObservacion(idx, editObs);
    }
    cancelEdit();
  };

  return (
    <Grid item xs={12}>
      <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>OBSERVACIONES</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '10%' }}>Acciones</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '50%' }}>Descripción</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '20%' }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '20%' }}>Usuario</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(observaciones || []).map((obs: Observacion, idx: number) => (
                <TableRow key={idx}>
                  <TableCell>
                    {editIndex === idx ? (
                      <>
                        <IconButton size="small" color="success" onClick={() => saveEdit(idx)}>
                          <Check fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="default" onClick={cancelEdit}>
                          <Close fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton size="small" color="primary" onClick={() => startEdit(idx, obs)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {editIndex === idx ? (
                      <TextField size="small" fullWidth value={editObs.descripcion} onChange={(e) => setEditObs({ ...editObs, descripcion: e.target.value })} />
                    ) : (
                      renderDescripcion(obs.descripcion)
                    )}
                  </TableCell>
                  <TableCell>
                    {editIndex === idx ? (
                      <TextField size="small" type="date" value={editObs.fecha} onChange={(e) => setEditObs({ ...editObs, fecha: e.target.value })} />
                    ) : (
                      obs.fecha
                    )}
                  </TableCell>
                  <TableCell>
                    {editIndex === idx ? (
                      <TextField size="small" value={editObs.usuario} onChange={(e) => setEditObs({ ...editObs, usuario: e.target.value })} />
                    ) : (
                      obs.usuario
                    )}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell>
                  <IconButton size="small" color="primary" onClick={agregarObservacion}>
                    <AddCircleOutline />
                  </IconButton>
                </TableCell>
                <TableCell>
                  <TextField 
                    size="small" 
                    fullWidth 
                    value={nuevaObservacion.descripcion} 
                    onChange={(e) => setNuevaObservacion({ ...nuevaObservacion, descripcion: e.target.value })} 
                    placeholder="Descripción de la observación" 
                  />
                </TableCell>
                <TableCell>
                  <TextField 
                    size="small" 
                    type="date" 
                    value={nuevaObservacion.fecha} 
                    onChange={(e) => setNuevaObservacion({ ...nuevaObservacion, fecha: e.target.value })} 
                  />
                </TableCell>
                <TableCell>
                  <TextField 
                    size="small" 
                    value={nuevaObservacion.usuario} 
                    onChange={(e) => setNuevaObservacion({ ...nuevaObservacion, usuario: e.target.value })} 
                    placeholder="Usuario" 
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Grid>
  );
};

export default ObservationsSection;